<script lang="ts" setup>
import * as locales from '@nuxt/ui/locale'
import { storeToRefs } from 'pinia'
import MobileBottomNav from '~/components/MobileBottomNav.vue'
import MobileTopBar from '~/components/MobileTopBar.vue'

const { locale } = useI18n()
const uiLocale = computed(() => locales[locale.value] || locales.en)

// 全局页面加载状态（SSR + 客户端路由切换）
useHead({
  meta: [
    { name: 'viewport', content: 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no' }
  ],
  link: [
    { rel: 'icon', href: '/favicon.png' }
  ],
  htmlAttrs: {
    lang: 'en'
  }
})

const router = useRouter()
const playerStore = usePlayerStore()
const uploadModalStore = useUploadModalStore()
const { open: uploadOpen, status: uploadStatus, progress: uploadProgress } = storeToRefs(uploadModalStore)
const shareModalStore = useShareModalStore()
const upgradeModalStore = useUpgradeModalStore()

const shareModalOpen = computed(() => shareModalStore.open)
const upgradeModalOpen = computed(() => upgradeModalStore.open)

const handleUploadModalUpdate = (value: boolean) => {
  if (!value) {
    uploadModalStore.cancel()
  }
}

const handleShareModalUpdate = (value: boolean) => {
  if (!value) {
    shareModalStore.closeShare()
  }
}

const handleUpgradeModalUpdate = (value: boolean) => {
  if (value) {
    upgradeModalStore.openModal()
  } else {
    upgradeModalStore.closeModal()
  }
}

// 未读通知数量轮询
const notificationStore = useNotificationStore()
const authStore = useAuthStore()
const musicPreviewStore = useMusicPreviewStore()
const route = useRoute()
let notificationPollingTimer: ReturnType<typeof setInterval> | null = null

const startNotificationPolling = () => {
  // 清除已有定时器
  if (notificationPollingTimer) {
    clearInterval(notificationPollingTimer)
    notificationPollingTimer = null
  }

  // 只在用户已登录时轮询
  if (authStore.isAuthenticated) {
    // 立即获取一次
    notificationStore.fetchUnreadCount()

    // 每30秒轮询一次
    notificationPollingTimer = setInterval(() => {
      if (authStore.isAuthenticated) {
        notificationStore.fetchUnreadCount()
      } else {
        // 如果用户已登出，停止轮询
        stopNotificationPolling()
      }
    }, 30000) // 30秒
  }
}

const stopNotificationPolling = () => {
  if (notificationPollingTimer) {
    clearInterval(notificationPollingTimer)
    notificationPollingTimer = null
  }
}

const isMobile = ref(false)
const preMountPlayer = ref(false)

const updateIsMobile = () => {
  if (typeof window === 'undefined') return
  isMobile.value = window.innerWidth < 768
}

const hideOnMobileGenerate = computed(() => {
  return isMobile.value && router.currentRoute.value.path.startsWith('/generate')
})

const shouldReservePlayerSpace = computed(() => {
  return !hideOnMobileGenerate.value && (!!playerStore.currentSong || preMountPlayer.value)
})

const handleResize = () => {
  updateIsMobile()
}

onMounted(async () => {
  // 只在客户端启动认证同步与用户信息拉取逻辑（SSR 不拉取用户信息）
  authStore.startClientAuth()

  updateIsMobile()

  // 预热音频播放模块，减少第一次点击播放时的卡顿
  try {
    if (typeof window !== 'undefined') {
      const warmup = () => playerStore.warmupHowler()
      if ('requestIdleCallback' in window) {
        ;(window as any).requestIdleCallback(warmup, { timeout: 1500 })
      } else {
        setTimeout(warmup, 800)
      }
    }
  } catch {}

  // 预挂载 MusicPlayer，避免第一次点击播放时触发首次 mount 带来的卡顿
  try {
    if (typeof window !== 'undefined') {
      const preMount = () => {
        if (!hideOnMobileGenerate.value) {
          preMountPlayer.value = true
        }
      }
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(preMount)
      } else {
        setTimeout(preMount, 0)
      }
    }
  } catch {}

  // 监听登录状态变化（仅客户端）
  watch(() => authStore.isAuthenticated, (isAuthenticated) => {
    if (isAuthenticated) {
      startNotificationPolling()
    } else {
      stopNotificationPolling()
      notificationStore.resetUnreadCount()
    }
  })

  // 路由切换时清空生成页右侧的预览内容（仅客户端）
  watch(() => route.fullPath, () => {
    musicPreviewStore.clearPreviewSong()
  })

  // 启动未读通知数量轮询
  if (authStore.isAuthenticated) {
    startNotificationPolling()
  }
  window.addEventListener('resize', handleResize)
})

// 组件卸载时清除定时器
onUnmounted(() => {
  stopNotificationPolling()
})
</script>

<template>
  <UApp
    :locale="uiLocale"
    :toaster="{ position: 'top-center' }"
  >
    <!-- Nuxt 自带顶部进度条（只在路由切换时出现） -->
    <NuxtLoadingIndicator />

    <MobileTopBar />

    <div class="md:flex w-full h-full overflow-hidden pt-[64px] md:pt-0">
      <!-- 侧边栏 -->
      <div class="h-full bg-[#262635] shadow-[0px_4px_4px_0px_#00000040] hidden md:block">
        <NavigationMenu />
      </div>
      <!-- 主体 -->
      <div
        :class="[
          'md:flex-1 w-full md:w-[calc(100%-200px)] h-full md:pb-0',
          shouldReservePlayerSpace ? 'pb-[60px]' : ''
        ]"
      >
        <NuxtPage :transition="true" />
        <MusicPlayer
          v-if="!hideOnMobileGenerate && (playerStore.currentSong || preMountPlayer)"
          v-show="playerStore.currentSong && !hideOnMobileGenerate"
        />
        <GlobalLoading />
      </div>
    </div>

    <MobileBottomNav />

    <!-- 全局登录弹窗 -->
    <LoginModal />

    <!-- 全局个人信息弹窗 -->
    <MyProfileModal />

    <!-- 全局分享弹窗 -->
    <InviteModal />

    <!-- 全局分享弹窗 -->
    <ShareModal
      :open="shareModalOpen"
      :song-title="shareModalStore.songTitle"
      :song-uuid="shareModalStore.songUuid"
      @update:open="handleShareModalUpdate"
    />

    <!-- 全局上传进度弹窗 -->
    <UploadProgressModal
      :open="uploadOpen"
      :status="uploadStatus"
      :progress="uploadProgress"
      @update:open="handleUploadModalUpdate"
      @cancel="uploadModalStore.cancel"
    />

    <!-- 全局升级订阅弹窗 -->
    <UpgradeModal
      :model-value="upgradeModalOpen"
      @update:model-value="handleUpgradeModalUpdate"
      @close="upgradeModalStore.closeModal()"
    />
  </UApp>
</template>
